import React, { Dispatch, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { PATHS } from 'src/common/constants';
import Actions from '../Actions';
import Overlay from '../Overlay';
import SuiteForm from '../SuiteForm';
import PropertyWalkThroughForm from '../PropertyWalkThroughForm';
import {
  setSuiteFormData,
  clearSuiteFormData,
} from 'src/store/actions/suiteForm';
import { selectSearch } from 'src/store/selectors/search';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import { selectPropertyWalkThroughFormIsVisible } from 'src/store/selectors/propertyWalkThroughForm';
import Loader from '../Loader';
import { selectFormLoader } from 'src/store/selectors/FormLoader';
import { startFormLoading, stopFormLoading } from 'src/store/actions/loader';
import * as client from '../../services/client';
import KeyHandoffComponent from 'src/pages/Suite/KeyHandoffComponent';
import SuitesActionSelect from 'src/components/SuiteActionSelect/SuiteActionSelect';
import { useBulkWalkthroughs } from 'src/components/SuitesTable/CustomHooks/useBulkWalkthroughs';
import { useBulkKeyPickups } from 'src/components/SuitesTable/CustomHooks/useKeyPickups';
import BulkAddWalkthroughsDialogue from 'src/components/BulkOperations/BulkAddWalkthroughsDIalogue/BulkAddWalkthroughsDialogue';
import BulkAddKeyPickupsDialogue from 'src/components/BulkOperations/BulkAddKeyPickupsDialogue/BulkAddKeyPickupsDialogue';
import SuitesResponsePopup from 'src/components/SuitesTable/SuitesResponsePopup';
import SuitesEmptyState from 'src/components/SuitesTable/SuitesEmptyState';
import SuitesTableView from 'src/components/SuitesTable/SuitesTableView';
import SuitesSearchBar from './SuitesSearchBar';
import { ISuite } from 'src/components/SharedTypes';
import {
  usePropertyRequestDataActionsFromContext,
  usePropertyRequestDataFromContext,
} from 'src/pages/Property/Context/PropertyContext';

const SuitesTable = (props: any) => {
  const [state, setState] = useState({
    showOverlay: false,
    showOverlayKeyHandoff: false,
    activatedOptions: ['Guided Walk Through', 'Self-Guided Walk Through'],
    suiteData: {},
  });

  const {
    saveBulkSuitesResponse,
    suitesError,
    isSuccess: bulkSaveSuccess,
  } = usePropertyRequestDataFromContext();

  const { clearResponse } = usePropertyRequestDataActionsFromContext();

  const [openBulkAddWalkthroughDialogue, setOpenBulkAddWalkthroughDialogue] =
    useState<boolean>(false);
  const [openBulkKeyPickupDialogue, setOpenBulkKeyPickupDialogue] =
    useState<boolean>(false);

  const {
    bulkWalkthroughsCreated,
    isLoading: walkthroughsLoading,
    isBulkCreationSuccess,
    error: walkthroughsError,
    saveBulkWalkthroughs,
    clearBulkWalkthroughsState,
  } = useBulkWalkthroughs();

  const {
    bulkKeyPickupsCreated,
    isLoading: keyPickupsLoading,
    error: keyPickupsError,
    saveBulkKeyPickups,
    clearBulkKeyPickupsState,
  } = useBulkKeyPickups();

  // Legacy Redux state - Todo: refactor this component to localize all this state.
  const search: any = useSelector(selectSearch);
  const user: any = useSelector(selectAuthenticatedUser);
  const propertyWalkThroughFormIsVisible: any = useSelector(
    selectPropertyWalkThroughFormIsVisible
  );
  const loader: any = useSelector(selectFormLoader);

  const dispatch: Dispatch<any> = useDispatch();
  const history: any = useHistory();
  const { showOverlay, showOverlayKeyHandoff } = state;

  const [searchTermFilterData, setSearchTermFilterData] = useState<string>('');

  const filteredSuiteData: ISuite[] = useMemo(() => {
    let filtered: ISuite[] = props.suites;

    if (user.roles[0].role.name === 'Home Owner') {
      filtered = filtered.filter(
        (suite: ISuite) => suite.homeownerId === user.id
      );
    }

    if (searchTermFilterData.trim()) {
      const searchTermLowercase: string = searchTermFilterData.toLowerCase();

      filtered = filtered.filter((suite: ISuite) => {
        const numberMatch = suite.number
          .toLowerCase()
          .includes(searchTermLowercase);
        const nameMatch =
          suite.building?.toLowerCase().includes(searchTermLowercase) || false;

        return numberMatch || nameMatch;
      });
    }

    return filtered;
  }, [searchTermFilterData, props.suites]);

  const handleAddSuite = () => {
    dispatch(
      setSuiteFormData({
        new: true,
        propertyId: props.property.id,
        old: false,
      })
    );
    setState({ ...state, showOverlay: true });
  };

  const handleOpenBulkWalkthroughsDialogue = () => {
    setOpenBulkAddWalkthroughDialogue(true);
  };

  const handleOpenBulkKeyPickupDialogue = () => {
    setOpenBulkKeyPickupDialogue(true);
  };

  const handleCloseSuiteForm = () => {
    setState({ ...state, showOverlay: false });
    dispatch(clearSuiteFormData());
  };

  const handleCloseKeyHandoff = () => {
    setState({ ...state, showOverlayKeyHandoff: false });
    dispatch(clearSuiteFormData());
  };

  const handleCloseBulkWalkthroughs = () => {
    setOpenBulkAddWalkthroughDialogue(false);
  };

  const handleCloseBulkKeyPickupDialogue = () => {
    setOpenBulkKeyPickupDialogue(false);
  };

  // Todo: Some pretty atrocious BOS legacy code here: https://app.asana.com/1/41213529744312/project/1208712340140532/task/1211000269523124?focus=true
  const fetchSuiteById = (id: any, action: any) => {
    dispatch(startFormLoading());
    client
      .getPropertySuiteById(id)
      .then((response: any) => {
        if (response.ok || response.status === 200) {
          response.json().then((responsejson: any) => {
            dispatch(stopFormLoading());
            if (action === 'edit') {
              dispatch(setSuiteFormData(responsejson));
              dispatch(setSuiteFormData({ old: true }));
              setState({ ...state, showOverlay: true });
            } else if (action === 'key') {
              dispatch(setSuiteFormData(responsejson));
              dispatch(setSuiteFormData({ old: true }));
              setState({ ...state, showOverlayKeyHandoff: true });
            } else {
              history.push({
                pathname: PATHS.SUITES_VIEW.replace(
                  ':id',
                  props.property.id + '&' + id
                ),
                state: { suite: responsejson, property: props.property },
              });
            }
          });
        } else {
          dispatch(stopFormLoading());
        }
      })
      .catch((e: any) => dispatch(stopFormLoading()));
  };

  return (
    <>
      {loader && <Loader center={true} />}
      <Actions compact>
        <SuitesSearchBar
          onSearch={(e: any) => setSearchTermFilterData(e.target.value)}
        >
          <SuitesActionSelect
            user={user}
            property={props.property}
            onAddSuite={handleAddSuite}
            handleOpenBulkWalkthroughsDialogue={
              handleOpenBulkWalkthroughsDialogue
            }
            handleOpenBulkKeyPickupDialogue={handleOpenBulkKeyPickupDialogue}
            fetchPropertySuites={props.fetchPropertySuites}
          />
        </SuitesSearchBar>
      </Actions>
      {filteredSuiteData.length > 0 ? (
        <SuitesTableView
          filteredSuites={filteredSuiteData}
          onSuiteClick={fetchSuiteById}
          onToggleMobileSync={props.onToggleMobileSync}
          fetchPropertySuites={props.fetchPropertySuites}
        />
      ) : (
        <SuitesEmptyState
          search={search}
          user={user}
          property={props.property}
          onAddSuite={handleAddSuite}
        />
      )}
      <Overlay
        showOverlay={showOverlay}
        content={
          <SuiteForm
            onClose={handleCloseSuiteForm}
            onSaveSuite={props.onSaveSuite}
          />
        }
      />
      <Overlay
        showOverlay={propertyWalkThroughFormIsVisible}
        content={<PropertyWalkThroughForm onSaveSuite={props.onSaveSuite} />}
      />
      <Overlay
        showOverlay={showOverlayKeyHandoff}
        content={
          <KeyHandoffComponent
            submitKeyHandoff={props.onSubmitKeyhandOffProcess}
            onClose={handleCloseKeyHandoff}
          />
        }
      />
      <BulkAddWalkthroughsDialogue
        isOpen={openBulkAddWalkthroughDialogue}
        onClose={handleCloseBulkWalkthroughs}
        userId={user.id}
        selectedSuiteIds={[]}
        onSave={saveBulkWalkthroughs}
        propertyId={props.property.id}
        isLoading={walkthroughsLoading}
        bulkWalkthroughsCreated={bulkWalkthroughsCreated}
        isBulkCreationSuccess={isBulkCreationSuccess}
        onSetBulkSuccess={clearBulkWalkthroughsState}
      />
      <BulkAddKeyPickupsDialogue
        isOpen={openBulkKeyPickupDialogue}
        onClose={handleCloseBulkKeyPickupDialogue}
        userId={user.id}
        selectedSuiteIds={[]}
        onSave={saveBulkKeyPickups}
        propertyId={props.property.id}
        isLoading={keyPickupsLoading}
        onSetBulkSuccess={clearBulkKeyPickupsState}
        bulkKeyPickupsCreated={bulkKeyPickupsCreated}
      />
      <SuitesResponsePopup
        isSuccess={bulkSaveSuccess}
        suitesResponse={saveBulkSuitesResponse}
        error={suitesError}
        onClear={clearResponse}
      />
    </>
  );
};

export default SuitesTable;
